// ─── SelectionFrame ────────────────────────────────────────────────────────
// Marco de selección reutilizable para clips dentro del preview del editor.
// Renderiza:
//   • un borde amarillo (brand-primary) alrededor del bounding box de su
//     contenedor (`inset: 0` — el caller posiciona el wrapper),
//   • cuatro corner-dots circulares + cuatro píldoras en cada lado, a modo
//     de manijas de resize,
//   • cada manija captura pointer events y emite callbacks con el delta
//     en pixels desde donde arrancó el drag, junto con el id del handle.
//
// Es agnóstico de la geometría real del clip: el caller decide qué
// significa "resize" (cambiar `widthPct` de un texto, `animScale` de una
// animación, etc.) y aplica la matemática correspondiente. SelectionFrame
// solo se ocupa de la UI y de propagar gestos.

import React, { useRef } from 'react';

export type SelectionHandle =
  | 'n'
  | 's'
  | 'e'
  | 'w'
  | 'ne'
  | 'nw'
  | 'se'
  | 'sw';

export type SelectionFramePhase = 'start' | 'move' | 'end';

export type SelectionFrameProps = {
  /** Cuáles manijas mostrar. Default: las 8 (4 esquinas + 4 lados).
   *  Para texto típicamente se omiten 'n' y 's' porque ahí no aporta:
   *  el alto se ajusta automáticamente al contenido. */
  handles?: SelectionHandle[];
  /** Callback unificado para drag de cualquier manija. dx/dy son deltas
   *  en pixels respecto al pointerdown. `phase` permite distinguir el
   *  inicio (commitar valor base), el move continuo y el final (típico
   *  para hacer un único `setClips` definitivo). */
  onResize?: (
    handle: SelectionHandle,
    dx: number,
    dy: number,
    phase: SelectionFramePhase,
  ) => void;
  /** Handle de rotación (círculo arriba del marco). Recibe la posición ABSOLUTA
   *  del puntero; el caller calcula el ángulo respecto del centro del elemento.
   *  Si no se pasa, no se muestra el handle. */
  onRotate?: (clientX: number, clientY: number, phase: SelectionFramePhase) => void;
  /** Color del marco. Default: amarillo brand-primary. */
  color?: string;
  /** Mostrar solo el borde sin manijas (para clips no editables). */
  outlineOnly?: boolean;
  /** true en touch: muestra un CÍRCULO de rotación visible (en vez de la zona +
   *  cursor de escritorio) y agranda las manijas para el dedo. */
  mobile?: boolean;
};

const ALL_HANDLES: SelectionHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

const CURSOR: Record<SelectionHandle, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
};

// Posiciones de cada handle como % sobre el bounding box. El wrapper
// del handle se centra en este punto (`translate(-50%,-50%)`).
const POS: Record<SelectionHandle, { left: string; top: string }> = {
  nw: { left: '0%', top: '0%' },
  n: { left: '50%', top: '0%' },
  ne: { left: '100%', top: '0%' },
  e: { left: '100%', top: '50%' },
  se: { left: '100%', top: '100%' },
  s: { left: '50%', top: '100%' },
  sw: { left: '0%', top: '100%' },
  w: { left: '0%', top: '50%' },
};

const isCorner = (h: SelectionHandle): boolean => h.length === 2;

// Cursor de rotación: UNA sola línea curva (arco) con una flecha en cada extremo
// apuntando a lados opuestos (← arco →), arqueada como abrazando la esquina que
// rota. Contorno blanco para verse sobre cualquier fondo.
const ROT_PATHS =
  "<path d='M3 14 Q12 6.5 21 14'/>" + // arco (la línea)
  "<path d='M3 14 L7 13 M3 14 L5.5 17.6'/>" + // punta izquierda
  "<path d='M21 14 L17 13 M21 14 L18.5 17.6'/>"; // punta derecha
// Genera el cursor con el arco ROTADO `deg` grados, para que abrace la esquina.
function rotCursor(deg: number): string {
  const svg =
    "<svg xmlns='http://www.w3.org/2000/svg' width='26' height='26' viewBox='0 0 24 24' fill='none' stroke-linecap='round' stroke-linejoin='round'>" +
    `<g transform='rotate(${deg} 12 12)'>` +
    `<g stroke='white' stroke-width='3.5'>${ROT_PATHS}</g>` +
    `<g stroke='black' stroke-width='1.8'>${ROT_PATHS}</g>` +
    '</g></svg>';
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 13 13, grab`;
}
// Un cursor por esquina: el arco (concavidad hacia abajo en 0°) se orienta hacia
// la esquina para que la abrace.
const ROT_CURSOR: Record<string, string> = {
  nw: rotCursor(-45),
  ne: rotCursor(45),
  se: rotCursor(135),
  sw: rotCursor(225),
};

export const SelectionFrame: React.FC<SelectionFrameProps> = ({
  handles = ALL_HANDLES,
  onResize,
  onRotate,
  color = '#FACC15', // amarillo brand-primary (~tailwind yellow-400)
  outlineOnly = false,
  mobile = false,
}) => {
  // Estado del drag actual. Vive en ref para no causar re-renders en cada
  // pointermove — el caller maneja su propio state vía onResize.
  const dragRef = useRef<{
    handle: SelectionHandle;
    pointerId: number;
    startX: number;
    startY: number;
  } | null>(null);

  const onDown = (handle: SelectionHandle) => (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    dragRef.current = {
      handle,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
    };
    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {}
    onResize?.(handle, 0, 0, 'start');
  };

  const onMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    onResize?.(d.handle, e.clientX - d.startX, e.clientY - d.startY, 'move');
  };

  const onUp = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    onResize?.(d.handle, e.clientX - d.startX, e.clientY - d.startY, 'end');
    try {
      (e.currentTarget as Element).releasePointerCapture(e.pointerId);
    } catch {}
    dragRef.current = null;
  };

  // Drag del handle de rotación. La rotación SOLO empieza tras arrastrar > umbral
  // (no con hover ni con un tap suelto): recién ahí se emite 'start'. `active`
  // marca que ya se superó el umbral.
  const ROT_THRESHOLD = 4;
  const rotRef = useRef<{ pointerId: number; startX: number; startY: number; active: boolean } | null>(
    null,
  );
  const onRotDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    rotRef.current = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, active: false };
    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {}
    // No se emite 'start' todavía: se espera a que el arrastre supere el umbral.
  };
  const onRotMove = (e: React.PointerEvent) => {
    const d = rotRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    if (!d.active) {
      if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < ROT_THRESHOLD) return;
      d.active = true;
      onRotate?.(e.clientX, e.clientY, 'start');
      return;
    }
    onRotate?.(e.clientX, e.clientY, 'move');
  };
  const onRotUp = (e: React.PointerEvent) => {
    const d = rotRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    if (d.active) onRotate?.(e.clientX, e.clientY, 'end');
    try {
      (e.currentTarget as Element).releasePointerCapture(e.pointerId);
    } catch {}
    rotRef.current = null;
  };

  return (
    <div
      // Borde alrededor del contenedor padre. `pointer-events-none` aquí
      // para que el contenedor (drag) siga recibiendo eventos cuando el
      // puntero pasa por encima de la línea del borde — solo las manijas
      // re-activan eventos.
      className="absolute inset-0 pointer-events-none"
      style={{
        outline: `1.5px solid ${color}`,
        outlineOffset: 0,
        // Sin background ni border para que el contenido interno se
        // siga viendo nítido — solo el outline.
      }}
    >
      {!outlineOnly &&
        handles.map(h => {
          const corner = isCorner(h);
          // Tamaños (más grandes en mobile para el dedo):
          //   • esquinas → círculo · lados N/S → píldora horizontal · E/W → vertical
          const dot = mobile ? 16 : 11;
          const long = mobile ? 26 : 20;
          const short = mobile ? 10 : 7;
          const w = corner ? dot : h === 'n' || h === 's' ? long : short;
          const ht = corner ? dot : h === 'n' || h === 's' ? short : long;
          const pos = POS[h];
          // Dirección diagonal hacia AFUERA de la esquina (para la zona de rotación).
          const rotDirX = h.includes('w') ? -1 : 1;
          const rotDirY = h.includes('n') ? -1 : 1;
          return (
            <React.Fragment key={h}>
              {/* Zona de rotación (SOLO desktop): por fuera de la esquina, con cursor
                  de flecha. En mobile va el círculo visible de abajo. */}
              {corner && onRotate && !mobile && (
                <div
                  onPointerDown={onRotDown}
                  onPointerMove={onRotMove}
                  onPointerUp={onRotUp}
                  onPointerCancel={onRotUp}
                  className="absolute pointer-events-auto"
                  style={{
                    left: pos.left,
                    top: pos.top,
                    width: 26,
                    height: 26,
                    transform: `translate(calc(-50% + ${rotDirX * 15}px), calc(-50% + ${rotDirY * 15}px))`,
                    cursor: ROT_CURSOR[h] ?? rotCursor(0),
                    touchAction: 'none',
                  }}
                />
              )}
              <div
                onPointerDown={onDown(h)}
                onPointerMove={onMove}
                onPointerUp={onUp}
                onPointerCancel={onUp}
                className="absolute pointer-events-auto"
                style={{
                  left: pos.left,
                  top: pos.top,
                  width: w,
                  height: ht,
                  transform: 'translate(-50%, -50%)',
                  background: color,
                  border: `1.5px solid ${color}`,
                  borderRadius: corner ? '50%' : 999,
                  cursor: CURSOR[h],
                  touchAction: 'none',
                  // Sombra sutil para que el handle se despegue del contenido del
                  // clip (texto / imagen) cuando el fondo es de color similar.
                  boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
                }}
              />
            </React.Fragment>
          );
        })}

      {/* Mobile: círculo de rotación visible, abajo del marco (arrastrable). */}
      {mobile && onRotate && (
        <div
          onPointerDown={onRotDown}
          onPointerMove={onRotMove}
          onPointerUp={onRotUp}
          onPointerCancel={onRotUp}
          className="absolute pointer-events-auto flex items-center justify-center"
          style={{
            left: '50%',
            top: '100%',
            marginTop: 26,
            width: 34,
            height: 34,
            transform: 'translate(-50%, -50%)',
            borderRadius: '50%',
            background: '#fff',
            border: `1.5px solid ${color}`,
            boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
            touchAction: 'none',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 14 Q12 6.5 21 14" />
            <path d="M3 14 L7 13 M3 14 L5.5 17.6" />
            <path d="M21 14 L17 13 M21 14 L18.5 17.6" />
          </svg>
        </div>
      )}
    </div>
  );
};
